import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import {
  Badge,
  Grid,
  Skeleton} from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IconCashRegister,
  IconEye,
  IconTrash
} from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useGetCashUpsReports } from '../../hooks/cashups/queries';
import { ICashUpReport, ICashierBox } from '../../interfaces/Store';
import { IUser } from '../../interfaces/User';
import { formatMoneyString } from '../../utils/strings';
import { useDeleteCashUpReportById } from '../../hooks/cashups/mutations';
import { showNotification } from '../../utils/Notification/Notification';
import { hasPermission } from '../../utils/permission-utils';
import CancelCashupReportModal from './CancelCashupReportModal';

const List = () => {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'Notifications', 'CashupsPage']);
  const { user } = useSelector((state) => state.user);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const queryClient = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList,
    isLoading
  } = useGetCashUpsReports(!!searchParamStoreId, searchParamStoreId, {
    limit: 9
  });

  const deleteById = useDeleteCashUpReportById();
  const [cashupToCancel, setCashupToCancel] = useState<ICashUpReport | null>(null);
  const canCreateCashupReports = hasPermission(user, 'cashupReports', 'canCreate');
  const canDeleteCashupReports = hasPermission(user, 'cashupReports', 'canDelete');

  const deleteItem = async (id: string, cancellationReason: string | null) => {
    try {
      if (!id) return;
      const res = await deleteById.mutateAsync({
        storeId: searchParamStoreId,
        id,
        cancellationReason
      });
      res && refetchList();
      setCashupToCancel(null);
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const cashupsReports = useMemo(() => {
    if (!data) return [];
    return data?.pages?.reduce((list, { cashUpReports }: { cashUpReports: ICashUpReport[] }) => {
      list.push(...cashUpReports);
      return list;
    }, [] as ICashUpReport[]);
  }, [data]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['cashups']
      });
    };
  }, []);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('CashupsPage:title')}
        createButtonText={t('CashupsPage:create')}
        showCreateButton={canCreateCashupReports}
        onCreateButtonClick={() =>
          history.push(`/cashUp/create?storeId=${searchParamStoreId}`, {
            storeID: searchParamStoreId
          })
        }
      />
      <LoaderOverlay visible={isLoading} />
      {cashupsReports && cashupsReports.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={cashupsReports?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {cashupsReports?.map((cashupReport: ICashUpReport) => {
              return (
                <Grid.Col key={cashupReport._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:cashierBox'),
                        value: (cashupReport.cashierBox as ICashierBox).name
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(cashupReport.createdDate).format('DD/MM/YYYY hh:mm')
                      },
                      {
                        label: t('FieldName:startingCashDate'),
                        value: moment(cashupReport.startingCashDate).format('DD/MM/YYYY hh:mm')
                      },
                      {
                        label: t('FieldName:leftInDrawer'),
                        value: formatMoneyString(cashupReport.leftInDrawer.toFixed(2)) || 0
                      },
                      {
                        label: t('FieldName:cashToBank'),
                        value: formatMoneyString(cashupReport.cashToBank.toFixed(2)) || 0
                      },
                      {
                        label: t('FieldName:createdBy'),
                        value: (cashupReport.createdBy as IUser).name
                      },
                      {
                        label: t('FieldName:notes'),
                        value: cashupReport.notes
                      },
                      {
                        label: t('FieldName:active'),
                        value: cashupReport.isActive ? (
                          <Badge color="green">{t('FieldName:active')}</Badge>
                        ) : (
                          <Badge color="red">{t('FieldName:canceled')}</Badge>
                        )
                      }
                    ]}
                    cardActionButtons={[
                      {
                        action: () =>
                          history.push(
                            `/cashUp/${cashupReport._id}?storeId=${searchParamStoreId}`,
                            {
                              storeID: searchParamStoreId
                            }
                          ),
                        disabled: false,
                        Icon: <IconEye />,
                        tooltip: t('CommonButtons:view')
                      },
                      {
                        action: () => setCashupToCancel(cashupReport),
                        disabled: deleteById.isPending,
                        hidden: !canDeleteCashupReports || cashupReport.isActive !== true,
                        loading: deleteById.isPending,
                        testId: `cashup-cancel-button-${cashupReport._id}`,
                        Icon: <IconTrash />,
                        tooltip: t('CommonButtons:cancel')
                      }
                    ]}
                    cardTitle={
                      `CC-${(cashupReport.cashierBox as ICashierBox).name}-${
                        cashupReport.reportNumber
                      }` || ''
                    }
                    TitleIcon={IconCashRegister}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
      <CancelCashupReportModal
        opened={!!cashupToCancel}
        isLoading={deleteById.isPending}
        onClose={() => setCashupToCancel(null)}
        onConfirm={(cancellationReason) =>
          deleteItem(cashupToCancel?._id || '', cancellationReason)
        }
      />
    </CatalogContainer>
  );
};

export default List;
